import React from 'react';
import { useCurrentUserPermissions } from '../hooks/useCurrentUserPermissions';

type ManagerPermissionKey =
  | 'manageResidents'
  | 'managePayments'
  | 'viewReports'
  | 'postAnnouncements'
  | 'approveApplications';

interface PermissionGuardProps {
  /**
   * The permission required to render children
   */
  permission: ManagerPermissionKey;
  /**
   * Content to render when user has permission
   */
  children: React.ReactNode;
  /**
   * Optional content to render when user doesn't have permission
   */
  fallback?: React.ReactNode;
  /**
   * How to handle unauthorized access
   * - 'hide': Don't render anything (default)
   * - 'disable': Render but disable the element
   */
  mode?: 'hide' | 'disable';
}

/**
 * Component to conditionally render based on user permissions
 * 
 * @example
 * // Hide button if user doesn't have permission
 * <PermissionGuard permission="manageResidents">
 *   <Button>Add Resident</Button>
 * </PermissionGuard>
 * 
 * @example
 * // Disable button if user doesn't have permission
 * <PermissionGuard permission="managePayments" mode="disable">
 *   <Button>Create Invoice</Button>
 * </PermissionGuard>
 * 
 * @example
 * // Show fallback message
 * <PermissionGuard 
 *   permission="viewReports" 
 *   fallback={<p>You don't have access to reports</p>}
 * >
 *   <ReportsContent />
 * </PermissionGuard>
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  children,
  fallback = null,
  mode = 'hide',
}) => {
  const { hasPermission } = useCurrentUserPermissions();

  const userHasPermission = hasPermission(permission);

  // User doesn't have permission
  if (!userHasPermission) {
    if (mode === 'hide') {
      return <>{fallback}</>;
    }

    // Disable mode: try to clone and add disabled prop
    if (mode === 'disable' && React.isValidElement(children)) {
      const childElement = children as React.ReactElement<React.HTMLAttributes<HTMLElement> & { disabled?: boolean }>;
      return React.cloneElement(childElement, {
        disabled: true,
        className: `${childElement.props?.className || ''} opacity-50 cursor-not-allowed`.trim(),
        title: 'You don\'t have permission for this action',
      });
    }

    // Fallback if can't disable
    return <>{fallback}</>;
  }

  // User has permission - render normally
  return <>{children}</>;
};

/**
 * Guard that requires ALL specified permissions
 */
interface MultiPermissionGuardProps extends Omit<PermissionGuardProps, 'permission'> {
  permissions: ManagerPermissionKey[];
  requireAll?: boolean; // If true, requires ALL permissions. If false, requires ANY permission
}

export const MultiPermissionGuard: React.FC<MultiPermissionGuardProps> = ({
  permissions,
  requireAll = true,
  children,
  fallback = null,
  mode = 'hide',
}) => {
  const { hasAllPermissions, hasAnyPermission } = useCurrentUserPermissions();

  const userHasPermission = requireAll
    ? hasAllPermissions(...permissions)
    : hasAnyPermission(...permissions);

  if (!userHasPermission) {
    if (mode === 'hide') {
      return <>{fallback}</>;
    }

    if (mode === 'disable' && React.isValidElement(children)) {
      const childElement = children as React.ReactElement<React.HTMLAttributes<HTMLElement> & { disabled?: boolean }>;
      return React.cloneElement(childElement, {
        disabled: true,
        className: `${childElement.props?.className || ''} opacity-50 cursor-not-allowed`.trim(),
        title: 'You don\'t have permission for this action',
      });
    }

    return <>{fallback}</>;
  }

  return <>{children}</>;
};
